import { useState, useRef, useEffect } from 'react';
import API from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { MagnifyingGlass, PaperPlaneTilt, Robot, User, Sparkle, SpinnerGap } from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function AISearchPage() {
  const [query, setQuery] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!query.trim() || loading) return;

    const userMessage = { role: 'user', content: query };
    setMessages(prev => [...prev, userMessage]);
    setQuery('');
    setLoading(true);

    try {
      const res = await API.post('/ai-search', { query: query.trim(), session_id: sessionId });
      setSessionId(res.data.session_id);
      setMessages(prev => [...prev, { role: 'assistant', content: res.data.response }]);
    } catch (err) {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Failed to get a response. Please try again.' }]);
      toast.error('AI search failed');
    } finally {
      setLoading(false);
    }
  };

  const suggestedQueries = [
    "How many active identities are there?",
    "Which roles have the most assignments?",
    "Show me recent access requests",
    "What workflows are currently in progress?",
    "List all entitlements by type",
    "Who has admin access?"
  ];

  return (
    <div data-testid="ai-search-page" className="p-6 md:p-8 h-[calc(100vh-3.5rem)] flex flex-col">
      <div className="mb-6">
        <h1 className="text-3xl font-black text-navy-900 tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
          AI Search
        </h1>
        <p className="text-sm text-navy-500 mt-1">Ask questions about identities, roles, entitlements, and governance data</p>
      </div>

      <div className="flex-1 flex flex-col bg-white border border-slate-200 rounded-sm shadow-sm overflow-hidden min-h-0">
        {/* Messages area */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-6 space-y-4">
          {messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <div className="w-16 h-16 rounded-sm bg-slate-900 flex items-center justify-center mb-4">
                <Sparkle size={28} className="text-orange-500" />
              </div>
              <h2 className="text-xl font-bold text-navy-900 mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                IAG Intelligence
              </h2>
              <p className="text-sm text-navy-500 max-w-md mb-6">
                Powered by Claude AI. Search across your governance data — identities, roles, entitlements, workflows, and audit logs.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-w-lg w-full">
                {suggestedQueries.map((sq, idx) => (
                  <button
                    key={idx}
                    onClick={() => setQuery(sq)}
                    className="text-left px-3 py-2 border border-slate-200 rounded-sm text-xs text-navy-600 hover:bg-slate-50 hover:border-orange-200 transition-colors"
                    data-testid={`suggested-query-${idx}`}
                  >
                    {sq}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            messages.map((msg, idx) => (
              <div key={idx} className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : ''}`}>
                {msg.role === 'assistant' && (
                  <div className="w-8 h-8 rounded-sm bg-slate-900 flex items-center justify-center shrink-0">
                    <Robot size={16} className="text-orange-500" />
                  </div>
                )}
                <div className={`max-w-[70%] rounded-sm p-4 ${
                  msg.role === 'user'
                    ? 'bg-orange-500 text-white'
                    : 'bg-slate-50 border border-slate-200 text-navy-700'
                }`}>
                  <pre className="text-sm whitespace-pre-wrap font-[inherit] leading-relaxed">{msg.content}</pre>
                </div>
                {msg.role === 'user' && (
                  <div className="w-8 h-8 rounded-sm bg-slate-200 flex items-center justify-center shrink-0">
                    <User size={16} className="text-navy-600" />
                  </div>
                )}
              </div>
            ))
          )}
          {loading && (
            <div className="flex gap-3">
              <div className="w-8 h-8 rounded-sm bg-slate-900 flex items-center justify-center shrink-0">
                <Robot size={16} className="text-orange-500" />
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-sm p-4 flex items-center gap-2">
                <SpinnerGap size={16} className="animate-spin text-orange-500" />
                <span className="text-sm text-navy-500">Analyzing governance data...</span>
              </div>
            </div>
          )}
        </div>

        {/* Input area */}
        <div className="border-t border-slate-200 p-4 bg-slate-50">
          <form onSubmit={handleSearch} className="flex gap-3">
            <div className="flex-1 relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 text-xs font-mono text-orange-500 font-bold">{'>'}_</div>
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Ask about your governance data..."
                className="pl-10 h-11 rounded-sm border-slate-200 bg-white font-mono text-sm focus:ring-orange-500/20 focus:border-orange-500"
                data-testid="ai-search-input"
                disabled={loading}
              />
            </div>
            <Button
              type="submit"
              disabled={loading || !query.trim()}
              className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm h-11 px-5"
              data-testid="ai-search-submit"
            >
              <PaperPlaneTilt size={16} />
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
